import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import "../../App.css";
import Menu from '../../panel/Menu';
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    updateDoc,
    doc,
    getCountFromServer,
    Timestamp,
    getDoc, setDoc, writeBatch
} from "firebase/firestore";
import moment from "moment";
import { auth, db } from '../../firebase';

import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Paper,
    IconButton,
    Pagination,
    Chip,
    InputAdornment,
    CircularProgress,
    Button,
    Avatar
} from "@mui/material";

import {
    Visibility,
    Search as SearchIcon,
    HotelOutlined,
    AssignmentTurnedIn
} from "@mui/icons-material";

function Otabookings() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    // Existing Component States
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);

    // Firebase Integration States
    const [propertyId, setPropertyId] = useState("");
    const [authCode, setAuthCode] = useState("");
    const [hotelCode, setHotelCode] = useState("");
    const [connected, setConnected] = useState(false);

    /* FETCH PROPERTY ID */
    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const uid = auth.currentUser?.uid;
                if (!uid) return;

                const docRef = doc(db, "authroles", uid);
                const snap = await getDoc(docRef);

                if (snap.exists()) {
                    setPropertyId(snap.data().propertyId);
                }
            } catch (error) {
                console.error("Error fetching property ID:", error);
            }
        };
        fetchProperty();
    }, []);

    /* FETCH CHANNEL MANAGER SETTINGS */
    useEffect(() => {
        const fetchChannelManager = async () => {
            if (!propertyId) return;
            try {
                const ref = doc(db, "channel_manager", propertyId);
                const snap = await getDoc(ref);

                if (snap.exists()) {
                    const data = snap.data();
                    setAuthCode(data.authCode || "");
                    setHotelCode(data.hotelCode || "");
                    setConnected(data.status === "connected");
                }
            } catch (error) {
                console.error("Error fetching channel manager settings:", error);
            }
        };
        fetchChannelManager();
    }, [propertyId]);

    /* SIMPLE LOG MIRROR (raw payload backup copy, NOT the real reservation record) */
    const autoSyncBookings = async (incomingBookings) => {
        if (!propertyId || incomingBookings.length === 0) return;
        try {
            const batch = writeBatch(db);
            let operationsCount = 0;

            for (const row of incomingBookings) {
                const resReservation = row.payload?.HotelReservations?.HotelReservation;
                const otaId = resReservation?.UniqueID?.ID || `ota-${row.id}`;

                const reservationRef = doc(db, "properties", propertyId, "ota_raw_logs", String(otaId));

                batch.set(reservationRef, {
                    otaLogId: row.id,
                    createdAt: row.createdAt || new Date().toISOString(),
                    syncStatus: "mirrored",
                    payload: row.payload
                }, { merge: true });

                operationsCount++;
            }

            if (operationsCount > 0) {
                await batch.commit();
                console.log(`Mirrored ${operationsCount} OTA logs to Firestore backup collection.`);
            }
        } catch (err) {
            console.error("Log mirror error:", err);
        }
    };

    /* FETCH AND FILTER OTA BOOKINGS */
    useEffect(() => {
        const fetchOtaBookings = async () => {
            if (!hotelCode) return;

            try {
                setLoading(true);
                const response = await fetch('https://booking.thekingsexecutive.com/api/webhooks-data');
                const result = await response.json();

                if (result.success && Array.isArray(result.data)) {
                    const todayStr = new Date().toISOString().split('T')[0];

                    // Step 1: Parse stringified payload data safely
                    const processedData = result.data.map(item => {
                        let parsedInnerData = {};
                        try {
                            parsedInnerData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
                        } catch (e) {
                            console.error("Error parsing inner webhook data string:", e);
                        }
                        return {
                            id: item.id,
                            createdAt: item.created_at,
                            payload: parsedInnerData
                        };
                    });

                    // Step 2: Filter by hotelCode, upcoming check-in date, & De-duplicate
                    const uniqueBookingsMap = new Map();

                    processedData.forEach(item => {
                        const resReservation = item.payload?.HotelReservations?.HotelReservation;
                        const otaId = resReservation?.UniqueID?.ID;
                        const checkInDate = resReservation?.RoomStays?.[0]?.TimeSpan?.Start;
                        const incomingHotelCode = resReservation?.RoomStays?.[0]?.BasicPropertyInfo?.HotelCode;

                        if (incomingHotelCode && String(incomingHotelCode).trim() === String(hotelCode).trim()) {
                            if (checkInDate && checkInDate >= todayStr) {
                                if (otaId) {
                                    if (!uniqueBookingsMap.has(otaId) || uniqueBookingsMap.get(otaId).id < item.id) {
                                        uniqueBookingsMap.set(otaId, item);
                                    }
                                } else {
                                    uniqueBookingsMap.set(`fallback-${item.id}`, item);
                                }
                            }
                        }
                    });

                    const finalUniqueArray = Array.from(uniqueBookingsMap.values()).sort((a, b) => b.id - a.id);
                    setBookings(finalUniqueArray);

                    // Automated background attempt to transfer into real reservations.
                    // Safe to call repeatedly: processAutomatedOtaBooking skips OTA IDs
                    // that have already been transferred (see idempotency check inside).
                    if (finalUniqueArray.length > 0 && propertyId) {
                        await processAutomatedOtaBooking(finalUniqueArray, propertyId);
                        await autoSyncBookings(finalUniqueArray);
                    }
                } else {
                    throw new Error(result.message || "Failed to fetch OTA logs.");
                }
            } catch (error) {
                console.error("Fetch error:", error);
                Toast.fire({
                    icon: "error",
                    title: error.message || "Something went wrong while fetching data."
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOtaBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotelCode, propertyId]);

    // Filter array by customer details search query matches (Mobile removed)
    const filteredBookings = bookings.filter((item) => {
        const resDetails = item.payload?.HotelReservations?.HotelReservation;
        const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;

        const givenName = customer?.PersonName?.GivenName || "";
        const surname = customer?.PersonName?.Surname || "";
        const customerName = `${givenName} ${surname}`.trim();

        const channelName = item.payload?.POS?.Source?.BookingChannel?.CompanyName || "";
        const otaId = resDetails?.UniqueID?.ID || "";

        const combinedSearch = `${customerName} ${channelName} ${otaId}`.toLowerCase();
        return combinedSearch.includes(searchText.toLowerCase());
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredBookings.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredBookings.length / rowsPerPage);

   const getChannelChip = (channel) => {
    const normalized = channel?.toLowerCase() || "";

    let logo = "";

    if (normalized.includes("booking")) {
        logo = "/logos/booking.png";
    } else if (
        normalized.includes("mmt") ||
        normalized.includes("makemytrip")
    ) {
        logo = "/logos/mmt.png";
    } else if (normalized.includes("goibibo")) {
        logo = "/logos/goibibo.png";
    } else if (normalized.includes("agoda")) {
        logo = "/logos/agoda.png";
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            {logo ? (
                <img
                    src={logo}
                    alt={channel}
                    style={{
                        width: 28,
                        height: 28,
                        objectFit: "contain",
                    }}
                />
            ) : (
                "-"
            )}
        </Box>
    );
};

    /* MANUAL SINGLE-ROW TRANSFER — routes through the same real allocation flow */
    const handleTransferToReservation = async (booking) => {
        if (!propertyId) return;
        await processAutomatedOtaBooking([booking], propertyId);
    };

    /* DETAILED VIEW POPUP */
    const handleViewDetails = (booking) => {
        const resDetails = booking.payload?.HotelReservations?.HotelReservation;
        const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
        const roomStay = resDetails?.RoomStays?.[0];

        const email = customer?.Email || "N/A";
        const commentText = resDetails?.ResGlobalInfo?.Comments?.Comment?.Text || roomStay?.Comments?.Comment?.Text || "None";
        const payoutType = roomStay?.DepositPayments?.GuaranteePayment?.Description?.Text || "N/A";

        // Dynamic Guest Count Array extraction
        const guestCounts = roomStay?.GuestCounts || [];
        const structuredGuests = guestCounts.map(g => {
            const label = g.AgeQualifyingCode === "10" ? "Adults" : "Children";
            return `${label}: ${g.Count}`;
        }).join(', ') || "N/A";

        Swal.fire({
            title: `<strong>Booking Details (ID: ${resDetails?.UniqueID?.ID || booking.id})</strong>`,
            html: `
                <div style="text-align: left; font-size: 0.9rem; max-height: 480px; overflow-y: auto; font-family: sans-serif; color: #334155;">
                    <div style="margin-bottom: 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">
                        <p style="margin: 4px 0;"><strong>Guest Email:</strong> ${email}</p>
                        <p style="margin: 4px 0;"><strong>Occupancy Breakdown:</strong> ${structuredGuests}</p>
                        <p style="margin: 4px 0;"><strong>Payment/Guarantee Basis:</strong> ${payoutType}</p>
                    </div>
                    <p style="margin: 4px 0; font-weight: bold; color: #1e293b;">Special Requests, Rates &amp; Conditions:</p>
                    <pre style="background: #f1f5f9; padding: 12px; border-radius: 6px; white-space: pre-wrap; font-family: monospace; font-size: 0.8rem; border: 1px solid #e2e8f0; margin-top: 6px;">${commentText}</pre>
                </div>
            `,
            showCloseButton: true,
            confirmButtonColor: "#3b82f6"
        });
    };

    const processAutomatedOtaBooking = async (otaBookingRows, propertyIdArg) => {
        if (!propertyIdArg || !Array.isArray(otaBookingRows) || otaBookingRows.length === 0) return;
        try {
            setLoading(true);

            for (const row of otaBookingRows) {
                const resReservation = row.payload?.HotelReservations?.HotelReservation;
                const roomStay = resReservation?.RoomStays?.[0];
                const otaId = String(resReservation?.UniqueID?.ID || row.id);

                if (!roomStay) {
                    console.warn(`Skipping OTA log ${row.id}: missing RoomStays data.`);
                    continue;
                }
                const existingQuery = query(collection(db, "reservations"), where("propertyId", "==", propertyIdArg),
                    where("otaRefId", "==", otaId));
                const existingSnap = await getDocs(existingQuery);
                if (!existingSnap.empty) {
                    continue;
                }

                const roomTypeCode = roomStay?.RoomTypes?.RoomType?.RoomTypeCode;
                if (!roomTypeCode) {
                    console.warn(`Skipping OTA ${otaId}: missing RoomTypeCode.`);
                    continue;
                }
                const roomTypeQuery = query(collection(db, "rooms"), where("propertyId", "==", propertyIdArg),
                    where("cmId", "==", String(roomTypeCode).trim()));
                const roomTypeSnap = await getDocs(roomTypeQuery);

                if (roomTypeSnap.empty) {
                    console.warn(`Skipping OTA ${otaId}: no local room type mapped to CM ID ${roomTypeCode}.`);
                    Toast.fire({ icon: "warning", title: `No room-type mapping for CM ID ${roomTypeCode} (OTA #${otaId})` });
                    continue;
                }

                const targetRoomTypeDoc = roomTypeSnap.docs[0];
                const targetRoomTypeId = targetRoomTypeDoc.id;
                const targetRoomTypeName = targetRoomTypeDoc.data().roomTypeName;

                const checkindate = roomStay?.TimeSpan?.Start;
                const checkoutdate = roomStay?.TimeSpan?.End;

                if (!checkindate || !checkoutdate) {
                    console.warn(`Skipping OTA ${otaId}: missing check-in/out dates.`);
                    continue;
                }

                const noofdays = Math.max(1, moment(checkoutdate).diff(moment(checkindate), 'days'));
                const roomInfosQuery = query(
                    collection(db, "roominfos"),
                    where("propertyId", "==", propertyIdArg),
                    where("roomTypeId", "==", targetRoomTypeId)
                );
                const roomInfosSnap = await getDocs(roomInfosQuery);

                if (roomInfosSnap.empty) {
                    console.warn(`Skipping OTA ${otaId}: no rooms configured under ${targetRoomTypeName}.`);
                    continue;
                }

                let availableRoomsList = roomInfosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                availableRoomsList.sort((a, b) =>
                    (a.roomNo || "").localeCompare(b.roomNo || "", undefined, { numeric: true, sensitivity: 'base' })
                );

                let allocatedRoom = null;

                for (const room of availableRoomsList) {
                    let isRoomBusy = false;

                    for (let day = 0; day < noofdays; day++) {
                        const targetDayStr = moment(checkindate).add(day, "days").format("YYYY-MM-DD");

                        const collisionQuery = query(
                            collection(db, "checkindetails"),
                            where("propertyId", "==", propertyIdArg),
                            where("roomid", "==", room.id),
                            where("checkindate", "==", targetDayStr),
                            where("status", "==", "Booked")
                        );
                        const collisionSnap = await getDocs(collisionQuery);

                        if (!collisionSnap.empty) {
                            isRoomBusy = true;
                            break;
                        }
                    }

                    if (!isRoomBusy) {
                        allocatedRoom = room;
                        break;
                    }
                }

                if (!allocatedRoom) {
                    console.warn(`Overbooking alert: no rooms free under ${targetRoomTypeName} for OTA #${otaId} (${checkindate} - ${checkoutdate}).`);
                    Toast.fire({ icon: "warning", title: `No rooms available for OTA #${otaId}` });
                    continue;
                }

                const bookingrooms = [{
                    roomid: allocatedRoom.id,
                    roomno: allocatedRoom.roomNo,
                    rtypeid: targetRoomTypeId,
                    roomtypename: targetRoomTypeName,
                    planid: roomStay?.RatePlans?.RatePlan?.RatePlanCode || "",
                    plananme: roomStay?.RatePlans?.RatePlan?.RatePlanName || "Standard OTA Plan"
                }];

                // --- Guest / customer details ---
                const customer = resReservation?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
                const givenName = customer?.PersonName?.GivenName || "";
                const surname = customer?.PersonName?.Surname || "";
                const custname = `${givenName} ${surname}`.trim() || "OTA Guest";

                const telephoneRaw = customer?.Telephone;
                const mobile = Array.isArray(telephoneRaw)
                    ? (telephoneRaw[0]?.PhoneNumber || telephoneRaw[0] || "")
                    : (telephoneRaw || "");

                const email = customer?.Email || "";

                const guestCounts = roomStay?.GuestCounts || [];
                const adults = guestCounts
                    .filter(g => g.AgeQualifyingCode === "10")
                    .reduce((sum, g) => sum + Number(g.Count || 0), 0) || 1;
                const children = guestCounts
                    .filter(g => g.AgeQualifyingCode !== "10")
                    .reduce((sum, g) => sum + Number(g.Count || 0), 0);

                const totalamount = Number(
                    roomStay?.Total?.AmountAfterTax || roomStay?.Total?.AmountBeforeTax || 0
                );
                const channel = row.payload?.POS?.Source?.BookingChannel?.CompanyName || "OTA";
                const commentText = roomStay?.Comments?.Comment?.Text || "";

                const customerData = {
                    custname, mobile, email,
                    address: customer?.Address?.AddressLine || "OTA Booking Platform",
                    city: customer?.Address?.CityName || "",
                    state: "",
                    gender: "Male",
                    gstname: "",
                    gstno: "",
                    documenttype: "",
                    idno: "",
                    propertyId: propertyIdArg,
                    uid: auth.currentUser?.uid || "SYSTEM_OTA",
                    createdAt: Timestamp.now(),
                };

                const customerRef = await addDoc(collection(db, "customers"), customerData);

                const invoiceCountQuery = query(collection(db, "reservations"), where("propertyId", "==", propertyIdArg));
                const invoiceSnap = await getCountFromServer(invoiceCountQuery);
                const invoiceNo = invoiceSnap.data().count + 1;

                const bookingPayload = {
                    customerId: customerRef.id,
                    invoiceno: invoiceNo,
                    customers: customerData,
                    rooms: bookingrooms,
                    services: [],
                    checkindate,
                    checkoutdate,
                    noofdays,
                    checkintime: "12:00 PM",
                    checkouttime: "11:00 AM",
                    adults: Number(adults),
                    childs: Number(children),
                    noofrooms: 1,
                    visitpurpose: "Business",
                    bookingsource: channel,
                    otaRefId: otaId,
                    otaLogId: row.id,
                    visitorPhotoUrl: "", idFrontUrl: "", idBackUrl: "", couplePhotoUrl: "",
                    rooamount: totalamount,
                    roundoff: 0,
                    totalamount,
                    advance: 0,
                    remaining: totalamount,
                    note: commentText || "Automated Channel Manager Integration Entry",
                    bookingDate: moment().format("YYYY-MM-DD"),
                    type: "GST",
                    status: "Booked",
                    approval1: "No", approval2: "No", approval3: "No", approval4: "No",
                    damageamount: 0,
                    propertyId: propertyIdArg,
                    uid: auth.currentUser?.uid || "SYSTEM_OTA",
                    createdAt: Timestamp.now(),
                };

                const bookingRef = await addDoc(collection(db, "reservations"), bookingPayload);

                // Save per-night breakdown into checkindetails
                for (let day = 0; day < noofdays; day++) {
                    const inDate = moment(checkindate).add(day, "days").format("YYYY-MM-DD");
                    const outDate = moment(checkindate).add(day + 1, "days").format("YYYY-MM-DD");

                    await addDoc(collection(db, "checkindetails"), {
                        bookingid: bookingRef.id,
                        customerId: customerRef.id,
                        checkindate: inDate,
                        checkoutdate: outDate,
                        propertyId: propertyIdArg,
                        roomid: allocatedRoom.id,
                        roomtypeid: targetRoomTypeId,
                        planid: bookingrooms[0].planid,
                        roomname: allocatedRoom.roomNo,
                        planname: bookingrooms[0].plananme,
                        roomtype: targetRoomTypeName,
                        status: "Booked",
                        uid: auth.currentUser?.uid || "SYSTEM_OTA",
                        createdAt: Timestamp.now(),
                    });
                }
                await updateDoc(doc(db, "roominfos", allocatedRoom.id), {
                    status: "Booked",
                });

                Toast.fire({ icon: "success", title: `Room ${allocatedRoom.roomNo} allocated for OTA #${otaId}` });
            }

        } catch (error) {
            console.error("Automated Allocation Failure:", error);
            Toast.fire({
                icon: "error",
                title: "Allocation Processing Error",
                text: error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Menu />

            <Box className="content-wrapper" p={3} sx={{ ml: "260px", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={700} color="#1e293b">
                        View Active OTA Bookings {hotelCode && `(Hotel Code: ${hotelCode})`}
                    </Typography>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => processAutomatedOtaBooking(currentRows, propertyId)}
                        sx={{
                            borderRadius: "12px",
                            px: 3,
                            py: 1.2,
                            fontWeight: 700,
                            textTransform: "none",
                            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                            boxShadow: "0 4px 12px rgba(37,99,235,0.2)",
                            "&:hover": { background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)" }
                        }}
                    >
                        Transfer to Reservations
                    </Button>
                </Box>

                <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.05)" }}>
                    <CardContent sx={{ p: 2 }}>

                        {/* Search Bar Input */}
                        <Box mb={3} maxWidth={360}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name, channel, ref ID..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor: "#f8fafc",
                                        fontSize: "0.85rem"
                                    }
                                }}
                            />
                        </Box>

                        {/* Async Operations Indicator */}
                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress size={36} sx={{ color: "#3b82f6" }} />
                            </Box>
                        ) : (
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Log ID</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Channel</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>OTA Ref ID</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Customer</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Check-in</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Check-out</TableCell>
                                            <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Room Category</TableCell>
                                            <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>No. of Rooms</TableCell>
                                            <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Total Amt</TableCell>
                                            <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentRows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                                    No active or upcoming bookings found for this property.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            currentRows.map((row) => {
                                                const resReservation = row.payload?.HotelReservations?.HotelReservation;
                                                const customer = resReservation?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
                                                const roomStay = resReservation?.RoomStays?.[0];

                                                const otaId = resReservation?.UniqueID?.ID || "N/A";
                                                const channel = row.payload?.POS?.Source?.BookingChannel?.CompanyName || "OTA";
                                                const givenName = customer?.PersonName?.GivenName || "";
                                                const surname = customer?.PersonName?.Surname || "";
                                                const fullName = `${givenName} ${surname}`.trim() || "Guest";

                                                const checkIn = roomStay?.TimeSpan?.Start || "N/A";
                                                const checkOut = roomStay?.TimeSpan?.End || "N/A";
                                                const roomName = roomStay?.RoomTypes?.RoomType?.RoomDescription?.Name || "N/A";
                                                const noOfRooms = roomStay?.RoomTypes?.RoomType?.NumberOfUnits || "1";
                                                const totalAmt = roomStay?.Total?.AmountBeforeTax || roomStay?.Total?.AmountAfterTax || "0";
                                                const currency = roomStay?.Total?.CurrencyCode || "INR";

                                                return (
                                                    <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{row.id}</TableCell>
                                                        <TableCell>{getChannelChip(channel)}</TableCell>
                                                        <TableCell sx={{ fontSize: "0.8rem", color: "#334155", fontFamily: "monospace" }}>{otaId}</TableCell>
                                                        <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>{fullName}</TableCell>
                                                        <TableCell sx={{ fontSize: "0.8rem", color: "#059669", fontWeight: 500 }}>{checkIn}</TableCell>
                                                        <TableCell sx={{ fontSize: "0.8rem", color: "#dc2626", fontWeight: 500 }}>{checkOut}</TableCell>
                                                        <TableCell sx={{ fontSize: "0.8rem", color: "#475569" }}>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <HotelOutlined sx={{ fontSize: 14, color: "#64748b" }} />
                                                                {roomName}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569" }}>
                                                            {noOfRooms}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1e293b" }}>
                                                            {currency} {totalAmt}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleViewDetails(row)}
                                                                    title="View Extended Details"
                                                                    sx={{ color: "#3b82f6", '&:hover': { backgroundColor: "#eff6ff" } }}
                                                                >
                                                                    <Visibility sx={{ fontSize: 18 }} />
                                                                </IconButton>

                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    startIcon={<AssignmentTurnedIn sx={{ fontSize: 14 }} />}
                                                                    onClick={() => handleTransferToReservation(row)}
                                                                    sx={{
                                                                        fontSize: "0.7rem",
                                                                        textTransform: "none",
                                                                        padding: "2px 8px",
                                                                        borderRadius: "6px",
                                                                        backgroundColor: "#10b981",
                                                                        '&:hover': { backgroundColor: "#059669" }
                                                                    }}
                                                                >
                                                                    Transfer
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Pagination Interface Controls */}
                        {!loading && totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(e, v) => setCurrentPage(v)}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Otabookings;
